import { isArray } from '../../../utils/array'
import { iterate } from '../../../utils/object'
import { queryString } from '../../../utils/window'
import { makeSafeFn, noop } from '../../../utils/fn'
import { userStore } from '../../stores/userStore'
import { getPlatform, viewport, domainFromFqdn, getIsMobileOrTablet } from '../../../utils/ua'
import { getShortLocale } from '../../../providers/i18nProvider/i18nProvider'
import { ADS_QUERYSTRING_OVERRIDE_PREFIXES, AD_FORMATS } from '../../config/adsConfig'
import { info, groupCollapsed, groupEnd } from '../../../application/console'
import { getCountryCode, NO_LOCATION } from '../../../providers/geolocationProvider'
import {
  get as getFromPdo,
  ads as adsFromPdo,
  getPageDomain,
} from '../../../application/pageDataObject'
import { unprefixQsProperty, getPossiblyOverriddenPropByQsWithPrefix } from '../../../utils/window'

const CUSTOM_CRITERIA_PREFIX = ADS_QUERYSTRING_OVERRIDE_PREFIXES.CUSTOM_CRITERIA
const DEBUG_CC = queryString.adsDebugCc
const DEFAULT_CC_VALUES = {
  AFFILIATE_ID: '0',
}
const getCcWithQsOverride = getPossiblyOverriddenPropByQsWithPrefix.part(CUSTOM_CRITERIA_PREFIX)
const DEBUG_CC_SPORTS_TYPES = queryString.adsDebugSportsTypes
const SPORTS_TYPE_FOOTBALL = 'Soccer'
const SPORTS_TYPE_AMERICAN = 'American'
const SPORTS_TYPE_NEWS = 'News'
const SPORTS_TYPE_DEFAULT = SPORTS_TYPE_FOOTBALL
const SPORTS_TYPE_BY_DOMAIN = {
  '12up': SPORTS_TYPE_AMERICAN,
  pluralist: SPORTS_TYPE_NEWS,
}
export const MONTI_PREROLL_AD_TYPE_PREFIX = 'preroll'
const MONTI_MIDROLL_AD_TYPE_PREFIX = 'midroll'
const MONTI_FIRST_PREROLLS_AD_TYPES = ['preroll0', 'preroll1']
const COUNTRY_CODE_OF_GERMANY = 'DE'
const SMARTSTREAM_PLACEMENT_IDS_SUPPORTED_COUNTIRES = ['CH', 'AT', COUNTRY_CODE_OF_GERMANY]
const SMARTSTREAM_MOBILE_PLACEMENT_IDS = {
  firstVideoAd: {
    DE: 'prer0-0%3D5289',
    CH: 'prer0-0%3D5429',
    AT: 'prer0-0%3D5427',
  },
  otherVideosAd: {
    DE: 'midr0-0%3D5293',
    CH: 'midr0-0%3D5551',
    AT: 'midr0-0%3D5547',
  },
}

function dfpTargetingValueToString(value) {
  if (isArray(value)) {
    return value
  }
  return value.toString()
}

function debugGroup(groupName) {
  DEBUG_CC && groupCollapsed(`Custom Criteria: ${groupName} ads`)
}

function debugGroupEnd() {
  DEBUG_CC && groupEnd()
}

function setTargetingViaTargetingFn(setTargetingFn, key, value) {
  DEBUG_CC && info('%c[CC] %s:', 'font-style: italic; color: green;', key, value)
  makeSafeFn(setTargetingFn).invoke(key, value)
}

function setCustomCriteria(setTargetingFn, customCriteriaValue, customCriteriaName) {
  const possiblyOverridenCcValue = getCcWithQsOverride(customCriteriaName, customCriteriaValue)
  if (possiblyOverridenCcValue) {
    setTargetingViaTargetingFn(setTargetingFn, customCriteriaName, possiblyOverridenCcValue)
  }
  return setTargetingFn
}

function addCustomCriteria(setTargetingFn, additionalCustomCriteriaKey) {
  return setCustomCriteria(setTargetingFn, undefined, additionalCustomCriteriaKey)
}

const removeCcPrefix = unprefixQsProperty.part(CUSTOM_CRITERIA_PREFIX)

function isUnknownCustomCriteria(qsKey, index, allQsKeys) {
  // this function has to be bound to customCriteriaMap to work
  return qsKey.startsWith(CUSTOM_CRITERIA_PREFIX) && !(removeCcPrefix(qsKey) in this)
}

const DEFAULT_ENV = 'production'

function getAdditionalCustomCriteriaKeys(customCriteriaMap) {
  return Object.keys(queryString)
    .filter(isUnknownCustomCriteria, customCriteriaMap)
    .map(removeCcPrefix)
}

function getLocaleNameForDfp() {
  return getShortLocale().toUpperCase()
}

let countryCode = NO_LOCATION
function setCurrentCountryCode(countryCodeResult) {
  countryCode = countryCodeResult
}

getCountryCode().then(setCurrentCountryCode).catch(noop)

function getCustomCriteriaMapFromQueryString(qs) {
  /* eslint-disable camelcase */
  const {
    a_aid: affiliateId = DEFAULT_CC_VALUES.AFFILIATE_ID,
    utm_medium: utmMedium,
    utm_source: utmSource,
    utm_campaign: utmCampaign,
    partnerId,
  } = qs
  // eslint-enable
  return {
    affiliateId,
    partnerId,
    utmMedium,
    utmSource,
    utmCampaign,
  }
}

function getSportsTypesByDomain(domain) {
  const singleSportsType = SPORTS_TYPE_BY_DOMAIN[domain] || SPORTS_TYPE_DEFAULT
  DEBUG_CC_SPORTS_TYPES &&
    info('[CC]\ncurrent domain: %s,\nsports-type: %s', domain, singleSportsType)
  return [singleSportsType]
}

function getPromotedMediaCc() {
  return getFromPdo('post.title', '').includes('VIDEO') ? 'video' : null
}

function getBaseCustomCriteriaMap() {
  const { mainLevels } = adsFromPdo.hierarchyLevels
  const baseCc = {
    // FIXME: not just football any longer!
    sportsTypes: getSportsTypesByDomain(domainFromFqdn()),
    language: getLocaleNameForDfp(),
    platform: getPlatform(),
    articleId: getFromPdo('post.id'),
    articleTemplate: getFromPdo('post.template'),
    env: getFromPdo('config.env', DEFAULT_ENV),
    categories: getFromPdo('post.categories'),
    viewportOrientation: viewport.orientation,
    adUnitPath: mainLevels,
    countryCode,
    markupFormat: 'web',
    promotedMedia: getPromotedMediaCc(),
    // TODOS:
    // contentProvider: 'ESPN'
    // sizes: requested slot sizes ['970x250', '300x600', ]
    // screenSize: viewport breakpoint
  }
  return {
    ...baseCc,
    ...getCustomCriteriaMapFromQueryString(queryString),
    ...mainLevels,
  }
}

const pageVideoCustomCriteria = new Map()

function addVideoCcKeyValuePairToThis(ccValue, ccKey) {
  const isCcValueStringable = ccValue !== undefined && ccValue.toString instanceof Function
  const value = isCcValueStringable ? ccValue.toString() : ''
  if (value.length) {
    this.push(`${ccKey}=${value}`)
  }
  return this
}

export function getVideoAdTargeting(instanceCustomCriteria = new Map()) {
  if (pageVideoCustomCriteria.size === 0) {
    const setTargetingFn = pageVideoCustomCriteria.set.bind(pageVideoCustomCriteria)
    const setVideoCc = setCustomCriteria.bind(null, setTargetingFn)
    debugGroup('video')
    const customCriteriaMap = getBaseCustomCriteriaMap()
    iterate(customCriteriaMap, setVideoCc)
    const additionalCustomCriteriaKeys = getAdditionalCustomCriteriaKeys(customCriteriaMap)
    const addVideoCc = addCustomCriteria.bind(null, setTargetingFn)
    additionalCustomCriteriaKeys.forEach(addVideoCc)
    debugGroupEnd()
  }
  const videoCustomCriteria = []
  pageVideoCustomCriteria.forEach(addVideoCcKeyValuePairToThis, videoCustomCriteria)
  instanceCustomCriteria.forEach(addVideoCcKeyValuePairToThis, videoCustomCriteria)

  let videoCustomCriteriaMap = {}
  videoCustomCriteria.forEach(function(cust_param) {
    let arr = cust_param.split('=')
    videoCustomCriteriaMap[arr[0]] = arr[1]
  })
  return videoCustomCriteriaMap
}

function adTypeParam(adType) {
  if (adType && adType.startsWith(MONTI_PREROLL_AD_TYPE_PREFIX)) {
    return `mm_preroll=${adType.substr(
      adType.indexOf(MONTI_PREROLL_AD_TYPE_PREFIX) + MONTI_PREROLL_AD_TYPE_PREFIX.length,
      adType.length
    )}`
  } else if (adType && adType.startsWith(MONTI_MIDROLL_AD_TYPE_PREFIX)) {
    return `mm_midroll=${adType.substr(
      adType.indexOf(MONTI_MIDROLL_AD_TYPE_PREFIX) + MONTI_MIDROLL_AD_TYPE_PREFIX.length,
      adType.length
    )}`
  } else {
    return ''
  }
}

function video_cust_param(adType) {
  let cust_param = getVideoAdTargeting()
  let str = ''
  for (var c in cust_param) {
    str += c + '=' + cust_param[c] + '&'
  }
  str += adTypeParam(adType)
  return encodeURIComponent(str)
}

function getAdUnitPath() {
  const { hierarchyLevels } = adsFromPdo
  hierarchyLevels.l4 = AD_FORMATS.Video
  return hierarchyLevels.toString()
}

export function getVideoAdServerUrl(description_url, adType) {
  let referel = window.location.href
  let parsedDescriptionUrl = description_url || referel
  return `https://pubads.g.doubleclick.net/gampad/ads?sz=640x360|480x270|400x300|640x480&iu=/${getAdUnitPath()}&impl=s&gdfp_req=1&env=vp&output=vast&unviewed_position_start=1&url=${referel}&description_url=${parsedDescriptionUrl}&correlator=${Date.now()}&cust_params=${video_cust_param(
    adType
  )}`
}

function getSmartstreamPlacementId(adType) {
  const videoKey = MONTI_FIRST_PREROLLS_AD_TYPES.includes(adType) ? 'firstVideoAd' : 'otherVideosAd'
  const countryCodeKey = SMARTSTREAM_PLACEMENT_IDS_SUPPORTED_COUNTIRES.includes(countryCode)
    ? countryCode
    : COUNTRY_CODE_OF_GERMANY
  return SMARTSTREAM_MOBILE_PLACEMENT_IDS[videoKey][countryCodeKey]
}

export function getPro7VideoAdServerUrl(adType) {
  let referral = encodeURIComponent(window.location.href)
  return `https://ads.smartstream.tv/r/?_f=vast2&_ps=${getSmartstreamPlacementId(
    adType
  )}&idt=100&ref=${referral}`
}

function shouldServePro7AdTag() {
  return (
    adsFromPdo.isProSeven &&
    getIsMobileOrTablet() &&
    (countryCode === NO_LOCATION ||
      SMARTSTREAM_PLACEMENT_IDS_SUPPORTED_COUNTIRES.includes(countryCode))
  )
}

export function getVideoTag(description_url, adType) {
  return shouldServePro7AdTag()
    ? getPro7VideoAdServerUrl(adType)
    : getVideoAdServerUrl(description_url, adType)
}

export default function setPageTargeting(gTag, additionalCcMap = {}) {
  const setTargetingFn = function getDfpSetTargeting(ccKey, ccValue) {
    ccValue = dfpTargetingValueToString(ccValue) // eslint-disable-line no-param-reassign
    if (DEBUG_CC === 'verbose') {
      info(
        '%c[CC] %s:',
        'font-style: italic; color: blue;',
        `ccKey: '${ccKey}', ccValue: '${ccValue}'`
      )
    }
    return gTag.pubads().setTargeting(ccKey, ccValue)
  }

  const setGtagCc = setCustomCriteria.bind(null, setTargetingFn)
  const customCriteriaMap = { ...getBaseCustomCriteriaMap(), ...additionalCcMap }
  debugGroup('display')
  iterate(customCriteriaMap, setGtagCc)
  const additionalCustomCriteriaKeys = getAdditionalCustomCriteriaKeys(customCriteriaMap)
  const addGtagCc = addCustomCriteria.bind(null, setTargetingFn)
  additionalCustomCriteriaKeys.forEach(addGtagCc)
  debugGroupEnd()
  return gTag
}



// WEBPACK FOOTER //
// ./provolution/app/views/provolution/jsCore/organizedByType/vms/ads/adTargeting.js